import { Modal, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Close } from '@/components/Icon';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { Flex } from '@/layout';

import { ErrorText, ModalForm } from '../styles';

interface ModalProps {
  showModal: boolean;
  busy: boolean;
  apiError: string;
  setShowModal: (show: boolean) => void;
  onSubmit: (reason: string) => Promise<void>;
}

const CancelToSignModal = ({ showModal, busy, apiError, setShowModal, onSubmit }: ModalProps) => {
  // @ts-ignore i18next's deeply nested translation key type exceeds CRA's incremental compiler limit.
  const { t: typedT } = useTranslation();
  const t = typedT as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { handleSubmit, reset, control } = useForm<{ reason: string }>({ defaultValues: { reason: '' } });

  const close = () => {
    if (busy) return;
    reset({ reason: '' });
    setShowModal(false);
  };

  return (
    <Modal open={showModal} onClose={close}>
      <ModalForm
        onSubmit={handleSubmit(async ({ reason }) => {
          await onSubmit(reason.trim());
          reset({ reason: '' });
        })}
      >
        <Flex marginBottom={30}>
          <Text fontWeight={700}>{t('signatureV2.actions.cancel')}</Text>
          <Close.ButtonClose type="button" disabled={busy} onClick={close}>
            <Close.CloseSVG />
          </Close.ButtonClose>
        </Flex>
        <Controller
          control={control}
          name="reason"
          rules={{ required: t('signatureV2.cancel.reasonRequired') }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t('form.reason.label')}
              variant="standard"
              sx={{ width: '100%' }}
              error={!!error}
              helperText={error?.message || null}
              autoComplete="off"
            />
          )}
        />
        {apiError && <ErrorText role="alert">{apiError}</ErrorText>}

        <Flex marginTop="50px" gridGap={30} alignItems="center" justifyContent="center">
          <Button type="submit" size="lg" variant="primary" disabled={busy}>
            {t('buttons.ok')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            color="primary"
            disabled={busy}
            onClick={close}
            style={{ textDecoration: 'underline' }}
          >
            {t('buttons.cancel')}
          </Button>
        </Flex>
      </ModalForm>
    </Modal>
  );
};

export default CancelToSignModal;
